import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

type CookieConsentLocale = AvailableLocales;

type CookieConsentTranslationKey =
  | 'text272'
  | 'text994'
  | 'text995'
  | 'text996'
  | 'text997'
  | 'text998'
  | 'text999'
  | 'text1000'
  | 'text1001'
  | 'text1002'
  | 'text1003'
  | 'text1004'
  | 'text1005'
  | 'text1006'
  | 'text1007'
  | 'text1008'
  | 'text1009'
  | 'text1010'
  | 'text1011'
  | 'text1012'
  | 'text1013'
  | 'text1014'
  | 'text1015'
  | 'text1016'
  | 'text1017';

type CookieConsentTranslationMap = Record<
  CookieConsentTranslationKey,
  Partial<Record<CookieConsentLocale, string>>
>;

export const COOKIE_CONSENT_TRANSLATIONS: CookieConsentTranslationMap = {
text272: {cs: 'Zavřít',en: 'Close'},
text994: {cs: 'Tato webstránka používá soubory cookies',en: 'This website uses cookies'},
text995: {cs: 'Pro přizpůsobení obsahu a reklam, poskytování funkcí sociálních médií a analýzu návštěvnosti používáme soubory cookie. Informace o tom, jak používáte naše webové stránky, poskytujeme také našim partnerům v oblasti sociálních médií, inzerce a analýzy. Tito partneři mohou příslušné informace zkombinovat s dalšími údaji, které jste jim poskytli nebo které od vás získali, když jste používali jejich služby.',en: 'We use cookies to personalise content and ads, provide social media features and analyse traffic. We also provide information about how you use our website to our social media, advertising and analytics partners. These partners may combine the relevant information with other information that you have provided to them or that they have collected from you when you have used their services.'},
text996: {cs: 'Povolit vše',en: 'Enable everything'},
text997: {cs: 'Odmítnout',en: 'Reject'},
text998: {cs: 'Přizpůsobit',en: 'Customize'},
text999: {cs: 'Nastavení cookies',en: 'Cookies settings'},
text1000: {cs: 'Služby',en: 'Services'},
text1001: {cs: 'Vaše možnosti ochrany soukromí',en: 'Your privacy options'},
text1002: {cs: 'V této části můžete vyjádřit některé preference týkající se zpracování vašich osobních údajů. Můžete kdykoli prozkoumat a změnit vyjádřené volby tím, že znovu zobrazíte tento panel prostřednictvím poskytnutého odkazu. Chcete-li odmítnout svůj souhlas se specifickými činnostmi zpracování popsanými níže, přepínače přepněte na vypnuté nebo použijte tlačítko „Odmítnout vše“ a potvrďte, že chcete uložit své volby.',en: 'In this section you can express some preferences regarding the processing of your personal data. You can review and change the expressed preferences at any time by revisiting this panel via the link provided. If you wish to refuse your consent to the specific processing activities described below, toggle the toggle switch to off or use the "Refuse All" button to confirm that you wish to save your choices.'},
text1003: {cs: 'Potřebné',en: 'Needed'},
text1004: {cs: 'Potřebné soubory cookie pomáhají vytvářet použitelné webové stránky tak, že umožňují základní funkce, jako je navigace stránky a přístup k chráněným oblastem webových stránek. Webové stránky nemohou řádně fungovat bez těchto souborů cookies.',en: 'Necessary cookies help to create usable websites by enabling basic functions such as site navigation and access to protected areas of the website. The website cannot function properly without these cookies.'},
text1005: {cs: 'Statistiky a analýzy',en: 'Statistics and analysis'},
text1006: {cs: 'Statistické soubory cookies pomáhají majitelům webových stránek, aby pochopili, jak komunikovat s návštěvníky webových stránek prostřednictvím shromažďování a hlášení informací anonymně.',en: 'Statistical cookies help website owners understand how to interact with website visitors by collecting and reporting information anonymously.'},
text1007: {cs: 'Tabulka souborů cookies',en: 'Cookie table'},
text1008: {cs: 'Cookie',en: 'Cookie'},
text1009: {cs: 'Doména',en: 'Domain'},
text1010: {cs: 'Marketing',en: 'Marketing'},
text1011: {cs: 'Marketingové soubory cookies se používají ke sledování návštěvníků na webových stránkách. Záměrem je zobrazovat reklamy, které jsou relevantní a poutavé pro jednotlivé uživatele, a tím cennější pro vydavatele a inzerenty třetích stran.',en: 'Marketing cookies are used to track visitors to the website. The intent is to serve ads that are relevant and engaging to individual users and thus more valuable to publishers and third-party advertisers.'},
text1012: {cs: 'Chci vědět více',en: 'I want to know more'},
text1013: {cs: 'Pro získání informací o zpracování osobních údajů a vašich právech v souvislosti s ochranou osobních údajů, nás můžete kontaktovat',en: 'For information about the processing of personal data and your rights in relation to the protection of personal data, you can contact us'},
text1014: {cs: 'zde.',en: 'here.'},
text1015: {cs: 'Funkční',en: 'Functional'},
text1016: {cs: 'Funkční soubory cookie pomáhají provádět určité funkce, jako je sdílení obsahu webové stránky na platformách sociálních médií, sbírání zpětné vazby a další funkce třetích stran.',en: 'Functional cookies help perform certain functions, such as sharing website content on social media platforms, collecting feedback and other third-party features.'},
text1017: {cs: 'Uložit nastavení',en: 'Save settings'}
};

function getCookieConsentText(locale: AvailableLocales, key: CookieConsentTranslationKey) {
  const translations = COOKIE_CONSENT_TRANSLATIONS[key];
  return translations[locale as CookieConsentLocale] || translations.en || key;
}

function createCookieConsentTranslator(locale: AvailableLocales) {
  return (key: CookieConsentTranslationKey) => getCookieConsentText(locale, key);
}

export const getCookieConsentConfig = (locale: AvailableLocales) => {
  const ct = createCookieConsentTranslator(locale);

  const config: CookieConsentConfig = {
    disablePageInteraction: false,
    autoShow: true,
    hideFromBots: true,
    mode: 'opt-in',

    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {},
      ads: {},
      functional: {},
    },

    language: {
      default: locale,
      translations: {
        [locale]: {
          consentModal: {
            title: ct('text994'),
            description: getConsentModalSummary(ct('text995')),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            showPreferencesBtn: ct('text998'),
          },
          preferencesModal: {
            title: ct('text999'),
            acceptAllBtn: ct('text996'),
            acceptNecessaryBtn: ct('text997'),
            savePreferencesBtn: ct('text1017'),
            closeIconLabel: ct('text272'),
            serviceCounterLabel: ct('text1000'),
            sections: [
              {
                title: ct('text1001'),
                description: ct('text1002'),
              },
              {
                title: ct('text1003'),
                description: ct('text1004'),
                linkedCategory: 'necessary',
              },
              {
                title: ct('text1015'),
                description: ct('text1016'),
                linkedCategory: 'functional',
              },
              {
                title: ct('text1005'),
                description: ct('text1006'),
                linkedCategory: 'analytics',
              },
              {
                title: ct('text1010'),
                description: ct('text1011'),
                linkedCategory: 'ads',
              },
              {
                title: ct('text1012'),
                description: `${ct('text1013')} <a href="${ROUTES.CONTACT()}" target="_blank">${ct(
                  'text1014',
                )}</a>`,
              },
            ],
          },
        },
      },
    },
  };

  return config;
};

function getConsentModalSummary(description: string) {
  const firstSentence = description.match(/^.*?[.!?](?:\s|$)/u)?.[0]?.trim();
  return firstSentence || description;
}
